/**
 * Lista preventivi (acquisto + vendita) con filtri.
 * Colonne mirate a riconoscere il preventivo a colpo d'occhio:
 * tipo, intestatario (cliente o fornitore), oggetto, importo
 * (a corpo se tipo_offerta=a_corpo, altrimenti totale), stato, PDF.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { Button, Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { useUO } from '@shell/UOContext';

interface Preventivo {
  id: string;
  numero: string;
  anno: number;
  tipo: 'acquisto' | 'vendita';
  tipo_offerta: 'a_corpo' | 'dettagliato' | 'misto';
  stato: string;
  scaduto: boolean;
  data_preventivo: string;
  data_validita: string | null;
  fornitore_id: string | null;
  cliente_id: string | null;
  richiesta_id: string | null;
  importo_totale: string;
  importo_a_corpo: string | null;
  oggetto: string | null;
  valuta: string;
  file_pdf_path: string | null;
  note: string | null;
  // Nome intestatario risolto lato server (Task 3 cantiere lista preventivi,
  // 2026-07-20). Opzionale (`?`) e non solo nullable: distingue "chiave
  // assente" (backend non ancora riavviato, vecchia forma di risposta) da
  // "valore None" (intestatario soft-deleted/orfano, risolto correttamente).
  intestatario_nome?: string | null;
}

const STATI_BADGE: Record<string, { label: string; className: string }> = {
  bozza: { label: 'Bozza', className: 'bg-muted text-muted-foreground' },
  in_revisione: { label: 'In revisione', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  inviato: { label: 'Inviato', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  approvato: { label: 'Approvato', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  ordine_generato: { label: 'Ordine generato', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
  rifiutato: { label: 'Rifiutato', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

// Debounce ricerca testuale (Task 5 cantiere lista preventivi, 2026-07-20):
// stesso ordine di grandezza del pattern gia' in uso in PreventivoDetail
// (ricerca articoli/distributori), ma qui si debouncia la SCRITTURA in URL
// (non solo la fetch): lo stato digitato vive in un buffer locale e solo
// allo scadere del timer diventa `q` in URL, cosi' la query string non si
// sporca a ogni tasto premuto e resta comunque la fonte di verita' per il
// fetch e per il tasto Indietro.
const DEBOUNCE_RICERCA_MS = 300;

// Dimensioni pagina selezionabili (Task 4): 200 e' il massimo accettato dal
// backend (ORDINI_MAX_PAGE_SIZE, vedi modules/ordini/.../config.py). Lista
// fissa di 3 valori: un <select> semplice basta, non serve un dropdown
// searchable (quella regola vale per i dropdown che pescano da una lookup).
const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const PAGE_SIZE_DEFAULT = 50;

// Anno di default del filtro: l'anno corrente, invariato dalla review
// finale del cantiere (deve restare il default anche ora che si puo'
// togliere il filtro, vedi ANNO_TUTTI sotto).
const ANNO_DEFAULT = new Date().getFullYear();

// Sentinella per "Tutti gli anni" nel <select>: DEVE essere un valore
// diverso dal default ('' non andrebbe bene). useUrlState toglie dalla URL
// il valore che coincide col default (vedi lib/useUrlState.ts): se la
// sentinella fosse '' selezionare "Tutti gli anni" verrebbe interpretato
// come "torna al default" e al tasto Indietro (o al refresh) si
// ritroverebbe silenziosamente filtrato sull'anno corrente invece che su
// tutti gli anni (review finale, punto 1 - proprio il caso "FRI Calabria"
// che ha originato il cantiere).
const ANNO_TUTTI = 'tutti';

// Opzioni anno derivate dall'anno corrente invece che da una lista di
// literal hardcoded (regola "mai hardcoding"): stessa ampiezza gia' in uso
// (2 anni indietro, 1 avanti) ma si aggiorna da sola col passare degli
// anni, senza bisogno di una chiamata API in piu' solo per questo dettaglio
// (segnalato in review, non e' il task per riprogettare la lookup anni).
const ANNI_OPZIONI = [ANNO_DEFAULT - 2, ANNO_DEFAULT - 1, ANNO_DEFAULT, ANNO_DEFAULT + 1];

function truncate(text: string | null, max: number): string {
  if (!text) return '';
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '...';
}

export function PreventiviList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currency, date } = useFormatters();
  const { currentUO } = useUO();
  const [preventivi, setPreventivi] = useState<Preventivo[]>([]);
  const [loading, setLoading] = useState(true);
  // `null` = header X-Total-Count assente dalla risposta (backend non ancora
  // riavviato, vecchia forma di risposta), DISTINTO da 0 = zero risultati
  // reali (fix bloccante #2, review post Task 5). Non inventare un totale
  // che non abbiamo: la UI deve distinguere i due casi.
  const [total, setTotal] = useState<number | null>(null);
  // Messaggio di errore onesto per rete/401/422 (fix consigliato #4): senza
  // questo la lista precedente resta a schermo senza alcun segnale che la
  // fetch e' fallita.
  const [errore, setErrore] = useState<string | null>(null);
  // Stato grezzo in URL: stringa numerica ('2025', ...) oppure ANNO_TUTTI.
  // Non si usa useUrlNumberState perche' deve poter rappresentare "nessun
  // filtro anno" in modo distinto dal default (vedi ANNO_TUTTI sopra).
  const [annoRaw, setAnnoRaw] = useUrlState('anno', String(ANNO_DEFAULT));
  // Anno numerico da inviare al backend: undefined = "Tutti gli anni",
  // nessun parametro `anno` in query (il backend gia' gestisce l'assenza
  // non filtrando, review finale punto 1).
  const anno = annoRaw === ANNO_TUTTI
    ? undefined
    : (Number.isNaN(Number(annoRaw)) ? ANNO_DEFAULT : Number(annoRaw));
  const [statoFiltro, setStatoFiltro] = useUrlState('stato', '');
  const [scadutoFiltro, setScadutoFiltro] = useUrlState('scaduto', '');
  const [q, setQ] = useUrlState('q', '');
  const [page, setPage] = useUrlNumberState('page', 1);
  const [pageSize, setPageSize] = useUrlNumberState('page_size', PAGE_SIZE_DEFAULT);
  // Tipo pre-filtrato dal path: /vendite/preventivi -> vendita, /preventivi -> acquisto
  // (path /preventivi e' nel gruppo "Acquisti" sidebar). Walter puo' comunque cambiare il filtro.
  const tipoIniziale = location.pathname.startsWith('/vendite/preventivi') ? 'vendita' : 'acquisto';
  const [tipoFiltro, setTipoFiltro] = useUrlState('tipo', tipoIniziale);

  // Buffer locale della ricerca (UNICA eccezione alla regola "stato in URL,
  // mai in useState"): il testo digitato vive qui, `q` in URL si aggiorna
  // solo a debounce scaduto. Se `q` cambia dall'esterno (es. back-navigation
  // che ripristina la URL) il buffer si riallinea.
  const [qBuffer, setQBuffer] = useState(q);
  useEffect(() => {
    setQBuffer(q);
  }, [q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (qBuffer !== q) {
        setQ(qBuffer);
        setPage(1);
      }
    }, DEBOUNCE_RICERCA_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qBuffer]);

  // Se cambio rotta (cliccando voce sidebar diversa), resetta il filtro tipo.
  // Il reset di `page` va fatto SOLO sul cambio vero e proprio, non al primo
  // mount (altrimenti una URL con ?page=4 arrivata da back-navigation
  // perderebbe la pagina): il ref tiene il pathname precedente per
  // distinguere le due situazioni.
  const pathnamePrecedente = useRef(location.pathname);
  useEffect(() => {
    setTipoFiltro(location.pathname.startsWith('/vendite/preventivi') ? 'vendita' : 'acquisto');
    if (pathnamePrecedente.current !== location.pathname) {
      setPage(1);
    }
    pathnamePrecedente.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Stesso discorso per il cambio ramo (unita' organizzativa, selezionato dal
  // menu in alto, fuori da questo componente): reset pagina solo sul cambio
  // reale, non al valore iniziale risolto al mount.
  const uoPrecedenteId = useRef(currentUO?.id ?? null);
  useEffect(() => {
    const attuale = currentUO?.id ?? null;
    if (uoPrecedenteId.current !== attuale) {
      setPage(1);
    }
    uoPrecedenteId.current = attuale;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUO?.id]);

  const token = localStorage.getItem('token');

  // Totale pagine noto solo se il backend ha esposto X-Total-Count. La
  // pagina richiesta dalla URL puo' essere fuori range (link diretto,
  // condiviso, o dati diminuiti da quando la URL e' stata salvata): la
  // limitiamo a [1, totalePagine] quando il totale e' noto, altrimenti solo
  // a un minimo di 1 (fix bloccante #3). Il valore limitato e' quello usato
  // sia per la fetch sia per il calcolo degli indici "da X a Y", cosi' i due
  // non possono mai disallinearsi.
  const totalePagine = total !== null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const paginaEffettiva = totalePagine !== null
    ? Math.min(Math.max(1, page), totalePagine)
    : Math.max(1, page);

  useEffect(() => {
    // AbortController per evitare race tra fetch concorrenti (fix bloccante
    // #1): senza, cliccare "Successiva" mentre la ricerca debounced e'
    // ancora in volo puo' far arrivare per ultima la risposta vecchia, che
    // sovrascrive quella nuova (tabella di pagina 1 col paginatore che dice
    // "Pagina 2") e sblocca i bottoni con setLoading(false) mentre la fetch
    // buona e' ancora in corso.
    const controller = new AbortController();

    const fetchPreventivi = async () => {
      setLoading(true);
      setErrore(null);
      const params = new URLSearchParams();
      // anno assente = "Tutti gli anni": il backend non filtra per anno
      // quando il parametro manca (review finale punto 1).
      if (anno !== undefined) params.set('anno', String(anno));
      if (statoFiltro) params.set('stato', statoFiltro);
      if (tipoFiltro) params.set('tipo', tipoFiltro);
      if (scadutoFiltro) params.set('scaduto', scadutoFiltro);
      if (currentUO?.id) params.set('unita_organizzativa_id', currentUO.id);
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(paginaEffettiva));
      params.set('page_size', String(pageSize));

      try {
        const res = await fetch(`/api/v1/preventivi?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (res.ok) {
          setPreventivi(await res.json());
          // Header esposto dal backend (Task 4): X-Total-Count, con
          // Access-Control-Expose-Headers gia' impostato lato server,
          // altrimenti fetch non lo vedrebbe. Assente (non solo vuoto) =
          // backend non ancora riavviato: null, MAI 0 (fix bloccante #2).
          const totaleHeader = res.headers.get('X-Total-Count');
          setTotal(totaleHeader !== null ? (parseInt(totaleHeader, 10) || 0) : null);
        } else {
          // Errore onesto (401 scaduto, 422, 5xx): non lasciare la lista
          // vecchia a schermo senza alcun segnale (fix consigliato #4).
          setErrore(`Errore ${res.status} nel caricamento dei preventivi`);
        }
      } catch (err) {
        // AbortError e' atteso (cleanup dell'effect su una fetch superata),
        // non e' un errore vero da mostrare a schermo.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setErrore('Errore di rete nel caricamento dei preventivi');
      } finally {
        // Se questa fetch e' stata abortita non tocchiamo `loading`: lo
        // sblocca la fetch successiva (in volo) quando completa lei.
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchPreventivi();

    return () => controller.abort();
  }, [anno, statoFiltro, tipoFiltro, scadutoFiltro, currentUO?.id, q, paginaEffettiva, pageSize, token]);

  const stati = ['', 'bozza', 'in_revisione', 'inviato', 'approvato', 'ordine_generato', 'rifiutato'];

  const daRecord = preventivi.length === 0 ? 0 : (paginaEffettiva - 1) * pageSize + 1;
  const aRecord = total !== null
    ? Math.min(paginaEffettiva * pageSize, total)
    : (paginaEffettiva - 1) * pageSize + preventivi.length;
  // "Successiva" senza totale noto: abilitato solo se questa pagina e'
  // piena (probabile che ce ne sia un'altra), disabilitato se i record
  // ricevuti sono meno della page size (ultima pagina, anche senza saperlo
  // dal totale).
  const successivaDisabilitata = loading || (totalePagine !== null
    ? paginaEffettiva >= totalePagine
    : preventivi.length < pageSize);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-outfit font-semibold text-foreground">
          Preventivi
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/preventivi/nuovo')}>
            Nuovo (acquisto)
          </Button>
          <Button onClick={() => navigate('/vendite/preventivi/nuovo')}>
            Nuovo (vendita)
          </Button>
        </div>
      </div>

      {currentUO && (
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          Ramo: <strong>{currentUO.nome}</strong> (selezionato dal menu in alto)
        </p>
      )}

      <div className="flex gap-3 flex-wrap">
        <input
          type="text"
          value={qBuffer}
          onChange={(e) => setQBuffer(e.target.value)}
          placeholder="Cerca per numero, oggetto, intestatario..."
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm w-64"
        />
        <select
          value={tipoFiltro}
          onChange={(e) => { setTipoFiltro(e.target.value); setPage(1); }}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          <option value="">Tutti i tipi</option>
          <option value="vendita">Vendita</option>
          <option value="acquisto">Acquisto</option>
        </select>
        <select
          value={anno === undefined ? ANNO_TUTTI : anno}
          onChange={(e) => { setAnnoRaw(e.target.value); setPage(1); }}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          <option value={ANNO_TUTTI}>Tutti gli anni</option>
          {ANNI_OPZIONI.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={statoFiltro}
          onChange={(e) => { setStatoFiltro(e.target.value); setPage(1); }}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {stati.map((s) => (
            <option key={s || '__all__'} value={s}>
              {s ? (STATI_BADGE[s]?.label || s) : 'Tutti gli stati'}
            </option>
          ))}
        </select>
        <select
          value={scadutoFiltro}
          onChange={(e) => { setScadutoFiltro(e.target.value); setPage(1); }}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          <option value="">Scadenza: tutti</option>
          <option value="true">Solo scaduti</option>
          <option value="false">Non scaduti</option>
        </select>
      </div>

      {errore && (
        <div className="rounded border border-red-300 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 px-3 py-2 text-sm">
          {errore}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
        ) : preventivi.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessun preventivo trovato
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Numero</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Tipo</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Intestatario</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Oggetto</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Stato</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Importo</th>
                <th className="text-center px-4 py-2 font-medium text-muted-foreground">PDF</th>
              </tr>
            </thead>
            <tbody>
              {preventivi.map((p) => {
                const badge = STATI_BADGE[p.stato] || { label: p.stato, className: 'bg-muted text-muted-foreground' };
                const isVendita = p.tipo === 'vendita';
                // `intestatario_nome` risolto lato server (Task 3). Se il
                // campo e' assente (chiave mancante, non null) il backend
                // non e' ancora stato riavviato: si mostra un placeholder
                // esplicito invece di una cella vuota indistinguibile da un
                // intestatario davvero orfano (valore null).
                const intestatario = p.intestatario_nome === undefined
                  ? '(aggiornamento in corso)'
                  : p.intestatario_nome;
                // Importo: a_corpo o importo_totale
                const importoVal = p.tipo_offerta === 'a_corpo' && p.importo_a_corpo
                  ? p.importo_a_corpo
                  : p.importo_totale;
                const importoEffettivo = parseFloat(importoVal) > 0 ? currency(importoVal) : '-';
                return (
                  <tr
                    key={p.id}
                    onClick={() => navigate(p.tipo === 'vendita' ? `/vendite/preventivi/${p.id}` : `/preventivi/${p.id}`)}
                    className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors duration-150 align-top"
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium whitespace-nowrap">{p.numero}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        isVendita
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300'
                      }`}>
                        {isVendita ? 'Vendita' : 'Acquisto'}
                      </span>
                      {p.tipo_offerta === 'a_corpo' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs ml-1 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200">
                          a corpo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{intestatario || <span className="text-muted-foreground italic">-</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground" title={p.oggetto || ''}>
                      {p.oggetto ? truncate(p.oggetto, 70) : <span className="italic">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">{date(p.data_preventivo)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                      {p.scaduto && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs ml-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" title="Validita' scaduta">
                          Scaduto
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                      {importoEffettivo}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.file_pdf_path ? (
                        <span className="text-emerald-600 dark:text-emerald-400 text-base" title="PDF disponibile in archivio">[PDF]</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Paginazione (Task 4): "non voglio perdere i vecchi" - la lista non
          tronca piu' in silenzio, il totale e i limiti pagina sono sempre
          visibili. */}
      <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
        <span className="text-muted-foreground">
          {total === null
            // Header X-Total-Count assente (fix bloccante #2): messaggio
            // onesto invece di inventare un totale, "Successiva" resta
            // comunque utilizzabile in base ai record ricevuti.
            ? (preventivi.length === 0
                ? 'Nessun risultato (totale non disponibile)'
                : `${daRecord} - ${aRecord}, totale non disponibile`)
            : total === 0
            ? 'Nessun risultato'
            : `Da ${daRecord} a ${aRecord} di ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, paginaEffettiva - 1))}
            disabled={paginaEffettiva <= 1 || loading}
          >
            Precedente
          </Button>
          <span className="text-muted-foreground px-1">
            Pagina {paginaEffettiva}{totalePagine !== null ? ` di ${totalePagine}` : ''}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(paginaEffettiva + 1)}
            disabled={successivaDisabilitata}
          >
            Successiva
          </Button>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} / pagina</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
